import React, { createContext, useContext } from 'react';
import { Link } from '@tanstack/react-router';
import { useRoleAccess } from '@/hooks/useRoleAccess';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { 
  Building2, 
  Package, 
  ShoppingCart, 
  Factory, 
  Boxes, 
  Truck, 
  Cpu, 
  Layers, 
  ClipboardCheck, 
  PackageCheck, 
  AlertTriangle,
  ExternalLink,
  FileText,
  Receipt,
  Warehouse
} from 'lucide-react';

export interface EntityLinkContextValue {
  disableLink?: boolean;
}

export const EntityLinkContext = createContext<EntityLinkContextValue>({
  disableLink: false,
});

export const useEntityLinkContext = () => useContext(EntityLinkContext);

export type EntityType = 
  | 'customer'
  | 'product'
  | 'order'
  | 'productionOrder'
  | 'material'
  | 'vendor'
  | 'machine'
  | 'qcProcess'
  | 'qcMaterial'
  | 'qcFinal'
  | 'ncr'
  | 'outsourceOrder'
  | 'delivery'
  | 'mold'
  | 'plate'
  | 'quotation'
  | 'receipt'
  | 'warehouse'
  | 'purchaseOrder'
  | 'warehouseDocument'
  | 'machineLog'
  | 'process'
  | 'layout'
  | 'spec'
  | 'design'
  | 'user';

export interface EntityLinkProps {
  entityType?: EntityType | string;
  /** Bí danh của entityType (một số nơi gọi `type`) */
  type?: EntityType | string;
  /** Bí danh của label (một số nơi gọi `name`) */
  name?: string | null;
  id?: string | null;           // UUID / CUID hoặc Mã dùng làm route param
  code?: string | null;         // Mã nghiệp vụ hiển thị (e.g. PO-1761, DH-001, QCP-2026-001)
  label?: string | null;        // Tên thực thể hiển thị (e.g. "Hộp Bánh Danisa")
  fallbackText?: string;
  showBadge?: boolean;
  variant?: 'link' | 'badge' | 'subtle';
  className?: string;
  showIcon?: boolean;
  /** Khi true, hiển thị nội dung/badge/icon nhưng KHÔNG bọc thẻ <Link> */
  disableLink?: boolean;
  /** Custom click handler (cho phép chặn điều hướng toàn trang để mở modal z-index khi cần) */
  onClick?: (e: React.MouseEvent) => void;
  target?: string;
  title?: string;
  /** Query params truyền vào route (ví dụ { returnUrl: ... }) */
  search?: Record<string, any>;
}

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const CUID_REGEX = /^c[a-z0-9]{20,32}$/i;

function isSurrogateId(str?: string | null): boolean {
  if (!str) return false;
  return UUID_REGEX.test(str) || CUID_REGEX.test(str);
}

function getEntityIcon(type: string) {
  switch (type) {
    case 'customer': return Building2;
    case 'product': return Package;
    case 'order': return ShoppingCart;
    case 'productionOrder': return Factory;
    case 'material': return Boxes;
    case 'vendor': return Truck;
    case 'machine': return Cpu;
    case 'qcProcess': return Layers;
    case 'qcMaterial': return ClipboardCheck;
    case 'qcFinal': return PackageCheck;
    case 'ncr': return AlertTriangle;
    case 'outsourceOrder': return Truck;
    case 'delivery': return Truck;
    case 'mold': return Boxes;
    case 'plate': return Layers;
    case 'quotation': return FileText;
    case 'receipt': return Receipt;
    case 'warehouse': return Warehouse;
    case 'purchaseOrder': return ShoppingCart;
    case 'warehouseDocument': return FileText;
    case 'machineLog': return Cpu;
    case 'process': return Layers;
    case 'layout': return Layers;
    case 'spec': return FileText;
    case 'design': return FileText;
    case 'user': return Building2;
    default: return ExternalLink;
  }
}

/**
 * EntityLink (Semantic Hyperlink)
 * 
 * Component liên kết thực thể dùng chung chuẩn ERP.
 * - Triệt tiêu 100% việc hiển thị chuỗi UUID/CUID ra giao diện.
 * - Điều hướng chính xác theo TanStack Router qua thẻ <Link> HTML thật.
 * - Chuột giữa mở tab mới, chuột phải sao chép liên kết hoạt động hoàn hảo.
 * - Tự động kiểm tra quyền đọc (RBAC) để hạ cấp an toàn về text nếu thiếu quyền.
 * - Hỗ trợ onClick để mở z-index modal khi gọi từ context modal.
 */
export const EntityLink: React.FC<EntityLinkProps> = ({ 
  entityType: entityTypeProp, 
  type: typeAlias,
  name: nameAlias,
  id, 
  code, 
  label: labelProp,
  fallbackText = '—', 
  showBadge = false,
  variant = showBadge ? 'badge' : 'link',
  className,
  showIcon = true,
  disableLink: directDisableLink,
  onClick,
  target,
  title,
  search,
}) => {
  const context = useContext(EntityLinkContext);
  const isLinkDisabled = directDisableLink ?? context.disableLink ?? false;
  const entityType = entityTypeProp || typeAlias || '';
  const label = labelProp ?? nameAlias;
  const { hasFullAccess, getDataScope } = useRoleAccess();
  
  // Chuẩn hóa loại thực thể (hỗ trợ cả format PascalCase như model Prisma)
  const normalizedType = (() => {
    const t = (entityType || '').toLowerCase();
    if (t === 'qcprocess' || t === 'qc_process') return 'qcProcess';
    if (t === 'qcmaterial' || t === 'qc_material') return 'qcMaterial';
    if (t === 'qcfinal' || t === 'qc_final') return 'qcFinal';
    if (t === 'productionorder' || t === 'production_order') return 'productionOrder';
    if (t === 'outsourceorder' || t === 'outsource_order' || t === 'outsourcepo') return 'outsourceOrder';
    if (t === 'purchaseorder' || t === 'purchase_order' || t === 'po' || t === 'purchasing') return 'purchaseOrder';
    if (t === 'warehousedocument' || t === 'warehouse_document' || t === 'inventorydocument' || t === 'inventory_document' || t === 'transaction') return 'warehouseDocument';
    if (t === 'machinelog' || t === 'machine_log') return 'machineLog';
    if (t === 'printlayout' || t === 'print_layout') return 'layout';
    if (t === 'technicalspec' || t === 'technical_spec') return 'spec';
    return t;
  })() as EntityType;

  let permissionTarget = '';
  let toPath = '';
  let paramKey = '';

  switch (normalizedType) {
    case 'customer':
      permissionTarget = 'master.customers';
      toPath = '/customers/$customerId';
      paramKey = 'customerId';
      break;
    case 'product':
      permissionTarget = 'master.products';
      toPath = '/products/$productId';
      paramKey = 'productId';
      break;
    case 'order':
      permissionTarget = 'sales.orders';
      toPath = '/orders/$orderId';
      paramKey = 'orderId';
      break;
    case 'productionOrder':
      permissionTarget = 'planning.production_orders';
      toPath = '/production-orders/$orderId';
      paramKey = 'orderId';
      break;
    case 'material':
      permissionTarget = 'master.materials';
      toPath = '/materials/$materialId';
      paramKey = 'materialId';
      break;
    case 'vendor':
      permissionTarget = 'outsourcing.vendors';
      toPath = '/vendors/$vendorId';
      paramKey = 'vendorId';
      break;
    case 'machine':
      permissionTarget = 'factory.machines';
      toPath = '/machines/$machineId';
      paramKey = 'machineId';
      break;
    case 'qcProcess':
      permissionTarget = 'quality.qc_process';
      toPath = '/qc-process/$checkId';
      paramKey = 'checkId';
      break;
    case 'qcMaterial':
      permissionTarget = 'quality.qc_materials';
      toPath = '/qc-materials/$checkId';
      paramKey = 'checkId';
      break;
    case 'qcFinal':
      permissionTarget = 'quality.qc_final';
      toPath = '/qc-final/$checkId';
      paramKey = 'checkId';
      break;
    case 'ncr':
      permissionTarget = 'quality.ncr';
      toPath = '/ncr/$ncrId';
      paramKey = 'ncrId';
      break;
    case 'outsourceOrder':
      permissionTarget = 'outsourcing.orders';
      toPath = '/outsource-orders/$orderId';
      paramKey = 'orderId';
      break;
    case 'delivery':
      permissionTarget = 'logistics.deliveries';
      toPath = '/delivery/$deliveryId';
      paramKey = 'deliveryId';
      break;
    case 'mold':
      permissionTarget = 'factory.molds';
      toPath = '/molds/$moldId';
      paramKey = 'moldId';
      break;
    case 'plate':
      permissionTarget = 'factory.plates';
      toPath = '/plates/$plateId';
      paramKey = 'plateId';
      break;
    case 'quotation':
      permissionTarget = 'sales.quotations';
      toPath = '/quotations/$quotationId';
      paramKey = 'quotationId';
      break;
    case 'receipt':
      permissionTarget = 'finance.receipts';
      toPath = '/finance/receipts/$receiptId';
      paramKey = 'receiptId';
      break;
    case 'warehouse':
      permissionTarget = 'warehouse.inventory';
      toPath = '/warehouse/master-data/$id';
      paramKey = 'id';
      break;
    case 'purchaseOrder':
      permissionTarget = 'purchasing.orders';
      toPath = '/purchasing/$poId';
      paramKey = 'poId';
      break;
    case 'warehouseDocument':
      permissionTarget = 'warehouse.inventory';
      toPath = '/transactions/$id';
      paramKey = 'id';
      break;
    case 'machineLog':
      permissionTarget = 'factory.machine_logs';
      toPath = '/machine-logs/$logId';
      paramKey = 'logId';
      break;
    case 'process':
      permissionTarget = 'system.processes';
      toPath = '/processes/$processId';
      paramKey = 'processId';
      break;
    case 'layout':
      permissionTarget = 'prepress.layouts';
      toPath = '/layouts/$layoutId';
      paramKey = 'layoutId';
      break;
    case 'spec':
      permissionTarget = 'technical.specs';
      toPath = '/specs/$specId';
      paramKey = 'specId';
      break;
    case 'design':
      permissionTarget = 'technical.designs';
      toPath = '/designs/$designId';
      paramKey = 'designId';
      break;
    case 'user':
      permissionTarget = 'system.users';
      toPath = '/users/$userId';
      paramKey = 'userId';
      break;
    default:
      toPath = '';
  }

  const canView = hasFullAccess || (permissionTarget && getDataScope(permissionTarget.split('.')[0]) !== 'NONE');

  // Lọc sạch UUID khỏi chuỗi hiển thị
  const cleanCode = isSurrogateId(code) ? null : code;
  const cleanLabel = isSurrogateId(label) ? null : label;

  // Quyết định văn bản hiển thị
  const displayText = (() => {
    if (cleanCode && cleanLabel) {
      return variant === 'badge' ? `${cleanCode} • ${cleanLabel}` : `${cleanCode} - ${cleanLabel}`;
    }
    if (cleanCode) return cleanCode;
    if (cleanLabel) return cleanLabel;
    // Nếu cả code và label đều rỗng nhưng có id
    if (id && !isSurrogateId(id)) return id;
    if (id && isSurrogateId(id)) return `[Xem chi tiết]`;
    return fallbackText;
  })();

  const IconComponent = showIcon ? getEntityIcon(normalizedType) : null;

  const handleClick = (e: React.MouseEvent) => {
    if (onClick) {
      // Nếu user click chuột giữa hoặc giữ Ctrl/Cmd, để browser mở tab mới
      if (e.button === 0 && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
        onClick(e);
      }
    }
  };

  // Badge Variant
  if (variant === 'badge') {
    const badgeColor = (() => {
      switch (normalizedType) {
        case 'productionOrder': return 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100';
        case 'order': return 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100';
        case 'customer': return 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100';
        case 'product': return 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100';
        case 'ncr': return 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100';
        case 'qcFinal': return 'bg-teal-50 text-teal-700 border-teal-200 hover:bg-teal-100';
        case 'qcProcess': return 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100';
        case 'qcMaterial': return 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100';
        case 'delivery': return 'bg-cyan-50 text-cyan-700 border-cyan-200 hover:bg-cyan-100';
        case 'mold': return 'bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100';
        case 'plate': return 'bg-sky-50 text-sky-700 border-sky-200 hover:bg-sky-100';
        case 'quotation': return 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100';
        case 'receipt': return 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100';
        case 'warehouse': return 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100';
        case 'purchaseOrder': return 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100';
        case 'warehouseDocument': return 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100';
        case 'machineLog': return 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100';
        default: return 'bg-slate-50 text-slate-700 border-slate-200';
      }
    })();

    const content = (
      <Badge 
        variant="outline" 
        className={cn(
          // Không tràn sang cột bên cạnh (chip co theo ô chứa) và không cắt "…": nhãn dài xuống dòng, đọc đủ mã + tên (golive 02/11/2026)
          "inline-flex items-start gap-1.5 font-medium px-2 py-0.5 text-xs transition-colors max-w-full min-w-0 shrink whitespace-normal text-left leading-snug justify-start rounded-md",
          badgeColor,
          className
        )}
        title={title}
      >
        {IconComponent && <IconComponent className="h-3 w-3 shrink-0 mt-[2px]" />}
        <span className="min-w-0 break-words" style={{ overflowWrap: "anywhere" }}>{displayText}</span>
      </Badge>
    );

    if (isLinkDisabled || !canView || !toPath || (!id && !code)) {
      return content;
    }

    const routeTargetId = id || code || '';
    return (
      <Link 
        to={toPath as any} 
        params={{ [paramKey]: routeTargetId } as any}
        search={search as any}
        className="inline-flex max-w-full"
        onClick={handleClick}
        target={target}
      >
        {content}
      </Link>
    );
  }

  // Subtle Variant
  if (variant === 'subtle') {
    const content = (
      <span className={cn("inline-flex items-start gap-1 text-slate-700 text-xs max-w-full min-w-0", className)} title={title}>
        {IconComponent && <IconComponent className="h-3 w-3 text-slate-400 shrink-0 mt-[2px]" />}
        <span className="font-medium min-w-0 break-words" style={{ overflowWrap: "anywhere" }}>{displayText}</span>
      </span>
    );

    if (isLinkDisabled || !canView || !toPath || (!id && !code)) {
      return content;
    }

    const routeTargetId = id || code || '';
    return (
      <Link 
        to={toPath as any} 
        params={{ [paramKey]: routeTargetId } as any}
        search={search as any}
        className="hover:text-indigo-600 transition-colors"
        onClick={handleClick}
        target={target}
      >
        {content}
      </Link>
    );
  }

  // Default Link Variant
  if (isLinkDisabled) {
    return (
      <span className={cn("inline-flex items-center gap-1 font-medium text-slate-800 max-w-full min-w-0", className)} title={title}>
        {IconComponent && <IconComponent className="h-3.5 w-3.5 shrink-0 text-slate-400" />}
        <span className="break-words min-w-0 whitespace-normal" style={{ overflowWrap: "anywhere" }}>{displayText}</span>
      </span>
    );
  }

  const content = (
    <span className={cn("inline-flex items-center gap-1 font-medium max-w-full min-w-0", className)} title={title}>
      {IconComponent && <IconComponent className="h-3.5 w-3.5 shrink-0 text-slate-400 group-hover:text-indigo-600" />}
      <span className="break-words min-w-0 whitespace-normal" style={{ overflowWrap: "anywhere" }}>{displayText}</span>
    </span>
  );

  if (!canView || !toPath || (!id && !code)) {
    return <span className="text-slate-600">{content}</span>;
  }

  const routeTargetId = id || code || '';
  return (
    <Link 
      to={toPath as any} 
      params={{ [paramKey]: routeTargetId } as any}
      search={search as any}
      className="group inline-flex max-w-full min-w-0 items-center text-indigo-600 hover:text-indigo-800 hover:underline transition-colors leading-snug cursor-pointer whitespace-normal"
      onClick={handleClick}
      target={target}
    >
      {content}
    </Link>
  );

};

export const EntityHyperlink = EntityLink;

export default EntityLink;
